import { useState, useEffect } from "react";
import { useQuery } from "@apollo/client";
import { useInView } from "react-intersection-observer";
import { Note } from "../helbest";

export const useInfiniteScroll = ({
  query,
  queryName,
  itemsPerPage = 20,
  clientName = null,
  variables = {},
  mapFunction = (data) => data,
}) => {
  const [offset, setOffset] = useState(0);
  const [allItems, setAllItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Setup intersection observer
  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0.5,
  });

  const {
    data,
    loading: initialLoading,
    fetchMore,
    error,
  } = useQuery(query, {
    variables: {
      first: itemsPerPage,
      offset: 0,
      ...variables,
    },
    context: {
      // headers: {
      //   "site-url": "garajmall.com",
      // },
      clientName: "wallet",
    },
    onError: (err) => {
      Note.error(err.message);
    },
  });

  // Handle initial data load
  useEffect(() => {
    if (data?.[queryName]) {
      const mappedData = mapFunction(data[queryName].edges);
      setAllItems(mappedData);
      setHasMore(data[queryName].pageInfo?.hasNextPage || false);
    }
  }, [data, queryName, mapFunction]);

  // Handle infinite scroll
  useEffect(() => {
    const loadMore = async () => {
      if (inView && hasMore && !initialLoading && !isLoadingMore) {
        const newOffset = offset + itemsPerPage;

        setIsLoadingMore(true);

        try {
          const fetchMoreResult = await fetchMore({
            variables: {
              offset: newOffset,
              first: itemsPerPage,
              ...variables,
            },
          });

          const newEdges = fetchMoreResult.data[queryName].edges;

          if (newEdges.length) {
            const mappedNewData = mapFunction(newEdges);
            setAllItems((prev) => [...prev, ...mappedNewData]);
            setOffset(newOffset);
            setHasMore(
              fetchMoreResult.data[queryName].pageInfo?.hasNextPage || false
            );
          } else {
            setHasMore(false);
          }
        } catch (err) {
          console.error("Error loading more items:", err);
        } finally {
          setIsLoadingMore(false);
        }
      }
    };

    loadMore();
  }, [
    inView,
    hasMore,
    initialLoading,
    isLoadingMore,
    offset,
    fetchMore,
    queryName,
    itemsPerPage,
    variables,
    mapFunction,
  ]);

  const reset = () => {
    setOffset(0);
    setAllItems([]);
    setHasMore(true);
    setIsLoadingMore(false);
  };

  return {
    items: allItems,
    loading: initialLoading || isLoadingMore,
    error,
    hasMore,
    loadMoreRef,
    reset,
  };
};
